import React from 'react'
import moment from 'moment'
import { Alert, Button, Card, CardBody, CardFooter } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamation } from '@fortawesome/free-solid-svg-icons'
import FalconCardHeader from '../../common/FalconCardHeader'
import FullPagination from '../../common/pagination/FullPagination'
import Flex from '../../common/Flex'
import { PreloadState } from '../../UI/preloading/PreloadState'
import { isIterableArray } from '../../../helpers/utils'
import useSWRFetchPagination from '../../../helpers/hooks/useSWRFetchPagination'
import { getRewardsTransactions } from '../../../helpers/api/rewards'
import { formatNumber, formatPeriod, getTransactionSource } from '../rewardsUtils'

// Referencia estable: forma parte de la clave de SWR, un objeto nuevo en cada
// render dispararía una consulta nueva cada vez.
const HISTORY_PARAMS = {}
// El backend pagina de a 15 por defecto y acepta hasta 100.
const PER_PAGE_OPTIONS = [15, 30, 50]

const Amount = ({ value, unit }) => {
  const amount = Number(value) || 0
  if (!amount) return null
  const positive = amount > 0

  return (
    <span className={`badge rounded-capsule ml-1 my-1 my-sm-0 ${positive ? 'badge-soft-success' : 'badge-soft-danger'}`}>
      {positive ? '+' : '−'}{formatNumber(Math.abs(amount))} {unit}
    </span>
  )
}

const TransactionRow = ({ transaction, isLast }) => {
  const source = getTransactionSource(transaction.source)
  const createdAt = moment(transaction.created_at)
  // Los movimientos de un pago traen su período en metadata.period ('2026-09'):
  // así "Pago de cuota aprobado" dice de qué mes es.
  const period = formatPeriod(transaction.metadata?.period)

  return (
    <Flex align="center" className={`px-3 py-2 ${isLast ? '' : 'border-bottom border-200'}`}>
      <div
        className={`d-flex flex-center rounded-circle mr-3 flex-shrink-0 bg-soft-${source.color} text-${source.color}`}
        style={{ width: '2.25rem', height: '2.25rem' }}
      >
        <FontAwesomeIcon icon={source.icon} />
      </div>
      <div className="flex-grow-1 mr-2">
        <p className="mb-0 font-weight-semi-bold text-900">
          {transaction.description || source.label}
          {period && <span className="font-weight-normal text-600"> · {period}</span>}
        </p>
        <span className="fs--1 text-500">
          {createdAt.isValid() ? createdAt.format('DD/MM/YYYY [·] h:mm A') : ''}
        </span>
      </div>
      {/* En móvil los montos se apilan para dejarle el ancho a la descripción. */}
      <div className="d-flex flex-column flex-sm-row align-items-end flex-shrink-0">
        <Amount value={transaction.hc_amount} unit="HC" />
        <Amount value={transaction.xp_amount} unit="XP" />
      </div>
    </Flex>
  )
}

const TransactionsHistory = () => {
  const {
    data,
    error,
    isLoading,
    meta,
    mutate,
    handler: { nextPage, previousPage, perPage, goToPage }
  } = useSWRFetchPagination(getRewardsTransactions, HISTORY_PARAMS, undefined, 1, PER_PAGE_OPTIONS[0])

  const hasItems = isIterableArray(data)

  // Al cambiar la cantidad por página se vuelve a la primera: quedarse en la
  // página actual puede caer fuera del nuevo total y mostrar la lista vacía.
  const handlePerPage = (value) => {
    perPage(value)
    goToPage(1)
  }

  const renderBody = () => {
    if (error) {
      return (
        <Alert color="danger" className="m-3 mb-0">
          No pudimos cargar tu historial de movimientos.
          <Button color="link" size="sm" className="ml-1 p-0 align-baseline" onClick={() => mutate()}>
            Reintentar
          </Button>
        </Alert>
      )
    }

    if (!hasItems) {
      return (
        <Alert color="info" className="m-3 mb-0 d-flex align-items-center">
          <FontAwesomeIcon icon={faExclamation} className="mr-3" />
          Aún no tienes movimientos. Cuando pagues tu condominio a tiempo, aquí verás las Coins y el XP que vas
          ganando.
        </Alert>
      )
    }

    return data.map((transaction, index) => (
      <TransactionRow key={transaction.id} transaction={transaction} isLast={index === data.length - 1} />
    ))
  }

  return (
    <Card className="mb-3">
      <FalconCardHeader title="Historial de movimientos" titleTag="h5" />
      <CardBody className="p-0 fs--1">
        {/* Al cambiar de página se deja la lista anterior con un velo encima
        en vez de vaciar la tarjeta, así la vista no salta mientras carga. */}
        <PreloadState overlay loading={isLoading} loadingClass="my-3">
          <div style={{ minHeight: isLoading && !hasItems ? '8rem' : undefined }}>
            {!(isLoading && !hasItems) && renderBody()}
          </div>
        </PreloadState>
      </CardBody>
      {hasItems && !error && (
        <CardFooter className="p-0">
          <FullPagination
            nextPage={nextPage}
            prevPage={previousPage}
            goToPage={goToPage}
            meta={meta}
            className="shadow-none"
            posText="movimientos"
            itemsPerPage={PER_PAGE_OPTIONS}
            perPage={handlePerPage}
          />
        </CardFooter>
      )}
    </Card>
  )
}

export default TransactionsHistory
